import flatten from 'lodash/flatten';
import {
    queryOptions,
    skipToken,
    useMutation,
    type DefaultError,
    type QueryClient,
    type UseMutationResult,
} from '@tanstack/react-query';

import { LegalEntityStatus, LegalEntityType } from 'chaika-enums';
import {
    LegalEntityCreateForm,
    LegalEntityData,
    LegalEntityListQuery,
    LegalEntityUpdateForm,
} from 'chaika-frontend-types/frontend-cms';
import { LegalEntityApi } from '@organization/api/LegalEntityApi';
import { store } from '@store';
import { queryClient } from '@store/queryClient';
import { userService } from '@user/services/userService';
import { getUniqChunkedIds } from '@utils/getUniqChunkedIds';
import { getUniqSortedIds } from '@utils/getUniqSortedIds';

const STALE_TIME_MS = Infinity;

export const legalEntityKeys = {
    all: ['legalEntity'] as const,
    lists: () => [...legalEntityKeys.all, 'list'] as const,
    list: (filters: LegalEntityListQuery) => [...legalEntityKeys.lists(), { filters }] as const,
    byIds: (ids: string[]) => [...legalEntityKeys.lists(), 'byIds', getUniqSortedIds(ids)] as const,
    details: () => [...legalEntityKeys.all, 'detail'] as const,
    detail: (id: string) => [...legalEntityKeys.details(), id] as const,
};

export const legalEntityQueryOptions = {
    list: listOptions,
    clinics: () => listOptions({ type: LegalEntityType.Clinic }),
    activeOnly: () => listOptions({ status: LegalEntityStatus.Active }),
    activeByType: (type: LegalEntityType) => listOptions({ status: LegalEntityStatus.Active, type }),
    forAccountant: (accountantIds: string[]) => listOptions({ accountantId: getUniqSortedIds(accountantIds).join() }),
    byIds: (ids: string[]) =>
        queryOptions({
            staleTime: STALE_TIME_MS,
            queryKey: legalEntityKeys.byIds(ids),
            queryFn:
                ids.length > 0
                    ? async () => {
                          const chunked = getUniqChunkedIds(ids);
                          const results = await Promise.all(
                              chunked.map(chunk => LegalEntityApi.getList({ id: chunk.join() })),
                          );
                          return flatten(results);
                      }
                    : skipToken,
        }),
    byId: (id: string | undefined) =>
        queryOptions({
            staleTime: STALE_TIME_MS,
            queryKey: legalEntityKeys.detail(id ?? ''),
            queryFn: id ? () => LegalEntityApi.get(id) : skipToken,
        }),
};

export function useCreateLegalEntity(): UseMutationResult<LegalEntityData, DefaultError, LegalEntityCreateForm> {
    return useMutation({
        mutationFn: async (params: LegalEntityCreateForm) => {
            await LegalEntityApi.create(params);
            return LegalEntityApi.get(params.id);
        },
        onSuccess: onMutationSuccess,
    });
}

export function useUpdateLegalEntity(): UseMutationResult<
    LegalEntityData,
    DefaultError,
    { id: string; params: LegalEntityUpdateForm }
> {
    return useMutation({
        mutationFn: async ({ id, params }) => {
            await Promise.all([
                LegalEntityApi.update(id, params),
                params.accountantIds ? userService.loadIfNotExist(params.accountantIds) : undefined,
            ]);
            return LegalEntityApi.get(id);
        },
        onSuccess: onMutationSuccess,
    });
}

export function useUpdateLegalEntityStatus(): UseMutationResult<
    LegalEntityData,
    DefaultError,
    { id: string; status: LegalEntityStatus }
> {
    return useMutation({
        mutationFn: async ({ id, status }) => {
            await LegalEntityApi.updateStatus(id, status);
            return LegalEntityApi.get(id);
        },
        onSuccess: onMutationSuccess,
    });
}

export function useUpdateCurrentLegalEntity(id: string | undefined) {
    const mutation = useUpdateLegalEntity();
    return async (params: LegalEntityUpdateForm): Promise<void> => {
        if (!id) return;
        await mutation.mutateAsync({ id, params });
    };
}

function onMutationSuccess(legalEntity: LegalEntityData): void {
    void queryClient.invalidateQueries({ queryKey: legalEntityKeys.lists() });
    queryClient.setQueryData(legalEntityKeys.detail(legalEntity.id), legalEntity);
    store.legalEntityList.add([legalEntity]);
}

function listOptions(filters: LegalEntityListQuery = {}) {
    return queryOptions({
        staleTime: STALE_TIME_MS,
        queryKey: legalEntityKeys.list(filters),
        queryFn: () => LegalEntityApi.getList(filters),
    });
}

// Reads matching legal entities out of any cached list query (scoped defaults or
// search results). Returns the full set only when every id resolves; a partial set
// would seed an incomplete byIds query that, with staleTime Infinity, never refetches
// the rest, so we return undefined instead and let the caller fetch.
export function collectLegalEntitiesByIdsFromCache(client: QueryClient, ids: string[]): LegalEntityData[] | undefined {
    if (ids.length === 0) return undefined;

    const byId = new Map<string, LegalEntityData>();
    const cachedListQueries = client.getQueriesData<LegalEntityData[]>({ queryKey: legalEntityKeys.lists() });
    for (const [, list] of cachedListQueries) {
        list?.forEach(le => byId.set(le.id, le));
    }

    const matched = ids.map(id => byId.get(id)).filter((le): le is LegalEntityData => le !== undefined);
    return matched.length === ids.length ? matched : undefined;
}

// Async LE selects (single / multi / creatable) call this on pick so consumers
// that read picked-LE data via `legalEntityQueryOptions.byIds([id])` (e.g. cross-field
// currency calcs in CreationMenus) resolve from cache instead of round-tripping. The
// picked LE is already cached under some other list query (scoped defaults or search).
export function primeLegalEntityByIdsCache(client: QueryClient, ids: string[]): void {
    if (ids.length === 0) return;

    const targetKey = legalEntityKeys.byIds(ids);
    if (client.getQueryData(targetKey)) return;

    const matched = collectLegalEntitiesByIdsFromCache(client, ids);
    if (matched) {
        client.setQueryData(targetKey, matched);
    }
}
